import { Component, computed, DestroyRef, effect, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContentScoringModalComponent } from 'listings/components/content-scoring-modal/content-scoring-modal.component';
import { ContentScoringService } from 'listings/services/content-scoring.service';
import { ListingsService } from 'listings/services/listings.service';
import { ListingsScrollUtilsService } from 'listings/services/listingsScrollUtils.service';
import { filter, map, take } from 'rxjs/operators';
import { ID_ZERO } from 'shared/constants/general-constants';
import { WebsocketConnectionKeys } from 'shared/enums/websocket-connection-keys.enum';
import {
    ChildItemViewModel,
    ContextAwareSidebarItem,
    ContextAwareSidebarType,
    CustomNavigationTarget,
    ItemViewModel,
} from 'shared/interfaces/context-aware-sidebar.interface';
import { ModalService } from 'shared/services';
import { ContextAwareSidebarService } from 'shared/services/context-aware-sidebar.service';
import { ListingPartsStatusService } from 'shared/services/listing-parts-status.service';
import { ListingComparisonOrchestratorService } from 'shared/services/listing-comparion-orchestrator.service';
import { SignalRService } from 'shared/services/signalR.service';
import { VisualsListingsService } from 'visuals/services/visual-listings.service';
import { ContextAwareSidebarActionsComponent } from '../context-aware-sidebar-actions/context-aware-sidebar-actions.component';
import { GaugeComponent } from '../gauge/gauge.component';

const CHILDREN_PREVIEW_LIMIT = 5;
const ROOM_TYPE_FEATURE_GROUP_FIELD_NAME = 'roomType';
const OUTLET_FEATURE_GROUP_FIELD_NAME = 'outlet';
const LISTING_OVERVIEW_FIELD_NAME = 'listingOverview';

@Component({
    selector: 'app-context-aware-sidebar',
    imports: [RouterLink, RouterLinkActive, TranslateModule, ContextAwareSidebarActionsComponent, GaugeComponent],
    templateUrl: './context-aware-sidebar.component.html',
    styleUrl: './context-aware-sidebar.component.scss',
})
export class ContextAwareSidebarComponent implements OnInit {
    listingId = this.contextAwareSidebarService.listingId;
    outletId = this.contextAwareSidebarService.outletId;
    listingName = computed(() => this.contextAwareSidebarService.listing()?.name ?? '');
    outletName = computed(() => this.contextAwareSidebarService.outlet()?.name ?? '');
    roomName = computed(() => this.contextAwareSidebarService.roomName()?.roomName ?? '');
    listingCode = computed(() => this.contextAwareSidebarService.listing()?.shijiId ?? '');
    sidebarType = input.required<ContextAwareSidebarType>();
    protected readonly ContextAwareSidebarType = ContextAwareSidebarType;

    titleToDisplay = computed(() => {
        switch (this.sidebarType()) {
            case ContextAwareSidebarType.LISTING:
                return this.listingName();
            case ContextAwareSidebarType.OUTLET:
                return this.outletName();
            case ContextAwareSidebarType.ROOM:
                return this.roomName();
            default:
                return '';
        }
    });

    searchQuery = signal('');

    private manualExpandedStates = signal<Record<string, string>>({});

    private currentUrlSignal = toSignal(
        this.router.events.pipe(
            filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
            map((navigationEnd) => navigationEnd.urlAfterRedirects),
        ),
        { initialValue: this.router.url },
    );

    private baseNavigationSegments = computed<(string | null)[]>(() => {
        const listingIdValue = this.listingId();
        const outletIdValue = this.outletId();
        const roomIdValue = this.contextAwareSidebarService.selectedRoomId();
        const sidebarTypeValue = this.sidebarType();

        if (sidebarTypeValue === ContextAwareSidebarType.BULK) {
            const bulkOperationIdValue = this.contextAwareSidebarService.bulkOperationId();
            return ['/', 'listings', 'bulk', bulkOperationIdValue];
        }
        if (sidebarTypeValue === ContextAwareSidebarType.OUTLET && outletIdValue) {
            return ['/', 'listings', 'details', listingIdValue, 'outlet', outletIdValue];
        }
        if (sidebarTypeValue === ContextAwareSidebarType.ROOM && roomIdValue) {
            return ['/', 'listings', 'details', listingIdValue, 'rooms', roomIdValue];
        }
        return ['/', 'listings', 'details', listingIdValue];
    });

    protected viewItems = computed<ItemViewModel[]>(() => {
        const manualExpanded = this.manualExpandedStates();
        const currentUrl = this.currentUrlSignal();
        const query = this.searchQuery().toLowerCase().trim();
        const highlightedLabels =
            this.sidebarType() === ContextAwareSidebarType.ROOM
                ? this.listingComparisonOrchestrator.highlightedRoomNavigationFieldNames()
                : this.listingComparisonOrchestrator.highlightedFeatureGroupLabels();
        const baseSegments = this.baseNavigationSegments();

        const currentUrlLastSegment = currentUrl.split('?')[0].split('/').filter(Boolean).pop() ?? '';

        return (this.contextAwareSidebarService.sidebarData() ?? [])
            .slice()
            .sort((firstItem, secondItem) => firstItem.order - secondItem.order)
            .map((item) => {
                const allChildren = item.children ?? [];

                const hasActiveChild = allChildren.some((child) => child.fieldName !== null && currentUrlLastSegment === child.fieldName);
                const isSelfActive = item.fieldName !== null && currentUrlLastSegment === item.fieldName;
                const isActive = isSelfActive || hasActiveChild;

                const isManuallyExpanded = manualExpanded[item.id] != null;

                const matchingChildren = query
                    ? allChildren.filter((child) => child.translationKey.toLowerCase().includes(query))
                    : allChildren;

                const parentMatchesQuery = !query || item.translationKey.toLowerCase().includes(query);
                const childrenMatchQuery = matchingChildren.length > 0;

                if (query && !parentMatchesQuery && !childrenMatchQuery) {
                    return null;
                }

                const childrenForDisplay = query && !parentMatchesQuery ? matchingChildren : allChildren;

                const isExpandedBySearch = query ? childrenMatchQuery : false;
                const isExpanded = hasActiveChild || isManuallyExpanded || (isSelfActive && allChildren.length > 0) || isExpandedBySearch;

                const shouldTruncate = item.totalChildrenCount != null && item.totalChildrenCount > CHILDREN_PREVIEW_LIMIT;
                const visibleChildrenSource = shouldTruncate ? childrenForDisplay.slice(0, CHILDREN_PREVIEW_LIMIT) : childrenForDisplay;
                const hiddenChildrenCount = shouldTruncate ? item.totalChildrenCount! - CHILDREN_PREVIEW_LIMIT : 0;

                const visibleChildren: ChildItemViewModel[] = visibleChildrenSource.map((child) => ({
                    ...child,
                    isHighlighted: child.fieldName !== null && highlightedLabels.has(child.fieldName),
                    routerLinkSegments: [...baseSegments, child.fieldName],
                }));

                const isSelfHighlighted = item.fieldName !== null && highlightedLabels.has(item.fieldName);
                const hasAnyHighlightedChild = allChildren.some(
                    (child) => child.fieldName !== null && highlightedLabels.has(child.fieldName),
                );
                const isHighlighted = isSelfHighlighted || hasAnyHighlightedChild;

                const showMoreRouterLinkSegments = item.fieldName ? [...baseSegments, item.fieldName] : null;

                return {
                    ...item,
                    isExpanded,
                    isActive,
                    isHighlighted,
                    visibleChildren,
                    hiddenChildrenCount,
                    showMoreRouterLinkSegments,
                };
            })
            .filter((item): item is ItemViewModel => item !== null);
    });

    isListingLive = this.listingPartsStatusService.allPartsLive;
    logoErrored = signal(false);

    listingLogo = computed(() => {
        const logoPath = this.contextAwareSidebarService.listing()?.supplierLogo;
        if (logoPath) {
            return logoPath;
        }
        return null;
    });

    showBackToListingButton = computed(
        () => this.sidebarType() !== ContextAwareSidebarType.LISTING && this.sidebarType() !== ContextAwareSidebarType.BULK,
    );

    backToListingRouterLink = computed(() => {
        let targetTab: string;
        switch (this.sidebarType()) {
            case ContextAwareSidebarType.ROOM:
                targetTab = ROOM_TYPE_FEATURE_GROUP_FIELD_NAME;
                break;
            case ContextAwareSidebarType.OUTLET:
                targetTab = OUTLET_FEATURE_GROUP_FIELD_NAME;
                break;
            default:
                targetTab = LISTING_OVERVIEW_FIELD_NAME;
        }
        return ['/', 'listings', 'details', this.listingId(), targetTab];
    });

    protected handleLogoError(event: Event): void {
        const imgElement = event.target as HTMLImageElement;
        imgElement.style.display = 'none';
        imgElement.parentElement?.classList.add('logo-fallback');
    }

    editingStatusInfo = computed(() => {
        const isListingLive = this.isListingLive();
        const sidebarType = this.sidebarType();

        if (sidebarType === ContextAwareSidebarType.BULK) {
            return {
                text: 'EDITING MULTIPLE LISTINGS',
                icon: 'far fa-pen fa-fw',
            };
        }

        const text =
            `${isListingLive ? 'viewing' : 'editing'} ${sidebarType === ContextAwareSidebarType.LISTING ? 'listing' : sidebarType}`.toUpperCase();
        return {
            text,
            icon: isListingLive ? 'far fa-eye fa-fw' : 'far fa-pen fa-fw',
        };
    });

    contentStatusInfo = this.listingPartsStatusService.contentStatusInfo;
    visualsStatusInfo = this.listingPartsStatusService.visualsStatusInfo;

    constructor(
        public contextAwareSidebarService: ContextAwareSidebarService,
        public contentScoringService: ContentScoringService,
        private listingPartsStatusService: ListingPartsStatusService,
        private visualsListingsService: VisualsListingsService,
        private listingComparisonOrchestrator: ListingComparisonOrchestratorService,
        private router: Router,
        private route: ActivatedRoute,
        private modalService: ModalService,
        private destroyRef: DestroyRef,
        private listingsService: ListingsService,
        private signalRService: SignalRService,
        private listingsScrollUtilsService: ListingsScrollUtilsService,
        private translate: TranslateService,
    ) {
        effect(() => {
            this.contextAwareSidebarService.listing();
            this.logoErrored.set(false);
        });
        effect(() => {
            const listingId = this.contextAwareSidebarService.listingId();
            if (listingId) {
                this.getContentScoringData(listingId);
            }
        });
        effect(() => {
            const scrollTarget = this.listingsScrollUtilsService.fieldToScrollTo();
            if (!scrollTarget || scrollTarget.featureGroupId === ID_ZERO || !scrollTarget.featureGroupFieldName) {
                return;
            }
            this.handleScrollToField();
        });
    }

    ngOnInit(): void {
        this.visualsListingsService.loadListingStatus.set(true);
    }

    private getContentScoringData(listingId: string) {
        this.listingsService
            .getContentScoringForListing(listingId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    if (data) {
                        this.contentScoringService.setScoreResult(data);
                    }
                },
            });
        this.signalRService.startConnectionByKey(WebsocketConnectionKeys.CONTENT_SCORE);
    }

    public openContentScoringModal() {
        this.modalService
            .openModal(
                ContentScoringModalComponent,
                {
                    type: 'center',
                },
                {
                    closeOnEscape: true,
                    closeOnOverlayClick: true,
                },
            )
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), take(1), filter(Boolean))
            .subscribe(() => {});
    }

    protected handleParentClick(item: ItemViewModel): void {
        if (item.children && item.children.length) {
            this.toggleExpanded(item.id);
        }

        if (item.fieldName) {
            this.router.navigate([...this.baseNavigationSegments(), item.fieldName]);
        }
    }

    protected handleChevronClick(event: Event, itemId: string): void {
        event.stopPropagation();
        this.toggleExpanded(itemId);
    }

    protected handleChildClick(item: ItemViewModel, child: ContextAwareSidebarItem): void {
        if (!item.fieldName || !child.fieldName) {
            return;
        }

        const baseSegments = this.baseNavigationSegments();

        switch (item.customNavigationTarget) {
            case CustomNavigationTarget.OUTLET:
                this.router.navigate([...baseSegments, item.fieldName, child.fieldName, 'outletProfile']);
                break;
            case CustomNavigationTarget.ROOM:
                this.router.navigate([...baseSegments, 'rooms', child.fieldName, 'roomOverview']);
                break;
        }
    }

    protected toggleExpanded(itemId: string): void {
        this.manualExpandedStates.update((states) => {
            const updatedStates = { ...states };
            if (updatedStates[itemId] != null) {
                delete updatedStates[itemId];
            } else {
                updatedStates[itemId] = itemId;
            }
            return updatedStates;
        });
    }

    protected updateSearchQuery(query: string): void {
        this.searchQuery.set(query);
    }

    private handleScrollToField(): void {
        const scrollTarget = this.listingsScrollUtilsService.fieldToScrollTo();
        if (!scrollTarget || !scrollTarget.featureGroupFieldName) {
            return;
        }

        const listingIdValue = this.listingId();
        if (!listingIdValue) {
            return;
        }

        const targetSegments = [...this.baseNavigationSegments(), scrollTarget.featureGroupFieldName];
        const targetUrl = targetSegments.filter((segment) => segment !== '/').join('/');
        const normalizedTargetUrl = `/${targetUrl}`;

        const currentUrlWithoutQuery = this.router.url.split('?')[0];

        if (currentUrlWithoutQuery === normalizedTargetUrl) {
            this.listingsScrollUtilsService.finalizeScrollToRetry();
            return;
        }

        this.router.navigate(targetSegments).then((navigationSucceeded) => {
            if (navigationSucceeded) {
                this.listingsScrollUtilsService.finalizeScrollToRetry();
            }
        });
    }
}
